import type { ChangeEvent, KeyboardEventHandler } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { TextareaAutosize } from '@mui/base/TextareaAutosize';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { InputControlsContext } from '@/components/BrandInsights/context';
import { ChatStateDispatchContext } from '@/components/Chat.State/context';
import { SoundInput } from '@/components/icons/SoundInput';
import { Switch, Tooltip } from '@/components/presentation';
import { useTheme } from '@/components/Theme';
import { AppThemingContext } from '@/containers/AppTheming/Context';
import { VoiceModeContext } from '@/containers/TextToSpeechAudioPlayer/Context';
import { useHasBetaPermission, useHasChatVoiceInputEnabled, useHasChatVoiceModeEnabled } from '@/store/selectors';
import { cx } from '@/utils';
import { pulse } from '@/utils/color';
import { shouldForwardProp } from '@/utils/emotion';
import { SubmitMessageContext, ChatSessionInputContext } from './context';
import type { MicState } from './hooks/useSpeechRecognition';
import { UploadFilesButton, uploadButtonHeight } from './Input.Upload';
import { SendIcon } from './SendIcon';
import styles from './style/VoiceMode.module.css';

type Props = {
  inputDisabled?: boolean;
};

export const UnsubmittedMessage = ({ inputDisabled }: Props) => {
  const ctx = useContext(InputControlsContext);
  const input = useContext(ChatSessionInputContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const theme = useTheme();
  const voicemode = useContext(VoiceModeContext);
  const { canSubmit, onSubmit: handleSubmit } = useContext(SubmitMessageContext);
  const hasBetaPermission = useHasBetaPermission();
  const hasChatVoiceInputEnabled = useHasChatVoiceInputEnabled();
  const { palette, theme: t } = useContext(AppThemingContext);

  // const uploadFiles = useContext(UploadFilesContext);
  // const isPreparingUpload = useContext(IsPreparingUploadContext);

  const disabled = useMemo(() => {
    return !canSubmit || inputDisabled;
  }, [
    canSubmit,
    inputDisabled,
  ]);

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = useCallback(e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        dispatch({
          type: 'input-changed',
          payload: {
            value: `${input}\n`,
          },
        });
      } else if (!disabled) {
        handleSubmit();
      }
    }
  }, [disabled, dispatch, handleSubmit, input/*, clearTimer*/]);

  const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    dispatch({
      type: 'input-changed',
      payload: { value: e.target.value },
    });
  }, [dispatch]);

  const micState = ctx.speechRecognition.micState;

  const MicButtonIcon = useCallback(() => {
    if (micState === 'listening' || micState === 'waiting-for-permission') {
      return <SoundInput
        ref={ctx.soundLevelRef}
        color={theme.palette.white.main}
        paused={micState === 'waiting-for-permission'} />;
    } else if (micState === 'error') {
      return <StyledMicOffIcon />;
    } else {
      return <StyledMicIcon />;
    }
  }, [micState, theme.palette.white.main]);

  const showVoiceFeatures = useMemo(() => {
    return ctx.speechRecognition.isSupported && hasBetaPermission && hasChatVoiceInputEnabled;
  }, [ctx.speechRecognition.isSupported, hasBetaPermission, hasChatVoiceInputEnabled]);

  const hasVoiceModeFeature = useHasChatVoiceModeEnabled();

  const color = voicemode?.enabled
    ? t?.enabled
      ? palette.primary.main
      : `var(--pri-03)`
    : `var(--gray-l)`;
  return (
    <Root>
      <Wrap>
        <TextArea
          ref={ctx.textAreaRef}
          disabled={inputDisabled}
          placeholder={ctx.speechRecognition.listening ? 'Speak your question.' : 'Type your question.'}
          minRows={3}
          maxRows={15}
          value={input}
          onKeyDown={handleKeyDown}
          onChange={handleChange} />

        <Buttons>
          <InlineButtons>
            {showVoiceFeatures && (
              <Tooltip title={micState === 'error' ? ctx.speechRecognition.errorMsg : null} placement='top'>
                <StyledMicButton
                  disabled={inputDisabled || micState === 'error'}
                  micState={micState}
                  onClick={ctx.onClickMicrophone}>
                  <MicButtonIcon />
                </StyledMicButton>
              </Tooltip>
            )}

            <Button
              disabled={disabled}
              onClick={ctx.onClickSubmit}>
              <StyledSendIcon
                disabled={disabled}
                size={40} />
            </Button>
          </InlineButtons>
          {hasVoiceModeFeature && (
            <VoiceModeButton>
              <VoiceModeInput>
                <div>Voice</div>
                <Switch
                  classes={{
                    root: cx(styles.switch, {
                      [styles.off]: !voicemode?.enabled,
                      [styles.on]: voicemode?.enabled,
                    }),
                    toggle: cx(styles.toggle, {
                      [styles.off]: !voicemode?.enabled,
                      [styles.on]: voicemode?.enabled,
                    }),
                  }}
                  className={styles.switch}
                  style={{ backgroundColor: color }}
                  active={voicemode?.enabled ? 'right' : 'left'}
                  labelLeft=""
                  labelRight=""
                  onClick={ctx.onClickVoiceMode} />
              </VoiceModeInput>
            </VoiceModeButton>
          )}
        </Buttons>
        {/* <StyledUploadButton
          isLoading={isPreparingUpload}
          onSelect={uploadFiles} /> */}
      </Wrap>
    </Root>
  );
};

const baseTextareaPadding = 10;

const submitBtnSize = 38;
const buttonsSpacing = 10;
const buttonsCount = 2;

const Root = styled('div')({
  // width: messageLaneWidth,
  width: '100%',
  margin: '0 auto',
  position: 'relative',
  // paddingTop: '70px',
});

const Wrap = styled.div({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  marginBottom: 10,
});

const TextArea = styled(TextareaAutosize)(({ theme }) => ({
  boxSizing: 'border-box',
  backgroundColor: theme.palette.white.main,
  borderRadius: 5,
  border: `2px solid ${theme.palette.gray.light2}`,
  outline: 'none',
  resize: 'none',
  padding: baseTextareaPadding,
  paddingRight: baseTextareaPadding + (submitBtnSize * buttonsCount) + (buttonsSpacing * (buttonsCount - 1)),
  paddingBottom: baseTextareaPadding + uploadButtonHeight,
  width: '100%',

  fontFamily: theme.fonts.regular,
  fontSize: 16,
  color: theme.palette.black.light1,
  transition: 'border 0.3s',

  '&:hover': {
    border: `2px solid ${theme.palette.primary.main}`,
  },

  '&:focus-visible': {
    outline: 'none',
  },

  '&:disabled': {
    cursor: 'not-allowed',
  },
}));

const Buttons = styled.div({
  // display: 'flex',
  // gap: buttonsSpacing,
  position: 'absolute',
  right: 10,
});

const InlineButtons = styled.div({
  display: 'flex',
  gap: buttonsSpacing,
});

const Button = styled.button`
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  width: ${submitBtnSize}px;
  height: ${submitBtnSize}px;
  border-radius: 50%;

  display: flex;
  justify-content: center;
  align-items: center;

  transition: background-color 0.3s;

  background-color: ${props => props.disabled ? props.theme.palette.gray.light1 : props.theme.palette.sentiment.primary.main};

  &:hover:not([disabled]) {
    background-color: ${({ theme }) => theme.palette.sentiment.primary.dark};
  }
`;

const VoiceModeButton = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  width: 100%;
  height: 40px;
`;

const VoiceModeInput = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-family: var(--font-bold);
`;

type SendProps = {
  disabled: boolean;
};

const StyledSendIcon = styled(SendIcon, { shouldForwardProp: shouldForwardProp('disabled') }) <SendProps>`
  padding-left: 4px;
  fill: ${props => props.disabled ? props.theme.palette.gray.dark1 : props.theme.palette.white.main};

  transition: fill 0.3s;
`;

const StyledMicIcon = styled(MicIcon, {})`
  color: var(--black-l);
`;

const StyledMicOffIcon = styled(MicOffIcon)`
  color: var(--black-l);
`;

type StyledMicButtonProps = {
  micState: MicState;
};

const StyledMicButton = styled('button')<StyledMicButtonProps>(({ theme, micState, disabled }) => ({
  cursor: disabled ? 'not-allowed' : 'pointer',

  width: `${submitBtnSize}px`,
  height: `${submitBtnSize}px`,
  borderRadius: '50%',

  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  transition: 'background-color 0.3s',

  boxShadow: `0 0 0 0 ${theme.palette.sentiment.primary.main}`,
  animation: micState === 'listening' ? `${pulse(theme.palette.sentiment.primary.main)} 4s infinite` : 'none',
  backgroundColor: micState === 'listening' || micState === 'waiting-for-permission' ? theme.palette.sentiment.primary.main : 'none',

  '&:hover:not([disabled])': {
    backgroundColor: micState === 'listening' || micState === 'waiting-for-permission' ? undefined : disabled ? theme.palette.gray.light1 : theme.palette.sentiment.primary.light,
  },

  '&:hover:not([disabled]) svg': {
    color: theme.palette.sentiment.primary.dark,
    //backgroundColor: listening ? undefined : disabled ? theme.palette.gray.light1 : theme.palette.sentiment.primary.light,
  },
}));

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StyledUploadButton = styled(UploadFilesButton)({
  position: 'absolute',
  bottom: baseTextareaPadding,
  left: baseTextareaPadding,
});